import { Button, Link, Box, IconButton } from "@mui/material";
import Alert from "@/components/alerts/Alert";
import Close from "@mui/icons-material/Close";
import WebshareApi from "../../api";
import { useNavigate } from "@tanstack/react-router";
import { useCommittedLocation } from "@/routing/committedLocation";
import { stringifySearch } from "@/routing/searchParams";
import useSWR from "@/hooks/useSWR";
import { SubuserType } from "@/types/SubuserTypes";
import { FlexBox, FlexCentered, RouteLink } from "../common/styles";

export default function SubUserBanner() {
  const navigate = useNavigate();
  // One clock: path and query both off the settled location, as Next published
  // them (ANO-079). Reading `search` off `state.location` while `pathname`
  // came from the committed one let `handleRemoveSubuserId` build the old path
  // with the new query, and let the banner decide `isSubUserRoute` from the
  // page being left while naming the sub-user of the page being entered.
  const location = useCommittedLocation();
  const pathname = location.pathname;
  const search = location.search as Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { subuser, ...restSearch } = search;

  const { data } = useSWR<SubuserType>({
    url: `/subuser/${subuser}/`,
    enabled: !!subuser,
  });

  const handleRemoveSubuserId = () => {
    WebshareApi.removeSubUserHeader();
    // Navigate by href, not `to: "."` — the latter resolves as a relative
    // *directory* and appends a trailing slash, which Next never did here
    // because it rebuilt the URL from the route pattern.
    void navigate({ href: `${pathname}${stringifySearch(restSearch)}` });
  };

  const isSubUserRoute =
    pathname.includes("/proxy/") || pathname.includes("/stats");

  if (!data || !data.id) return null;

  return (
    <Box sx={{ px: 5, pt: 5 }}>
      <FlexBox>
        <Alert
          action={
            isSubUserRoute ? (
              <Button
                disableElevation
                color="inherit"
                size="small"
                variant="contained"
                onClick={handleRemoveSubuserId}
              >
                Stop Viewing As This User
              </Button>
            ) : (
              <FlexCentered>
                <RouteLink
                  to="/subuser/user/$userid/detail"
                  params={{ userid: String(data.id) }}
                >
                  <Button
                    disableElevation
                    color="inherit"
                    size="small"
                    variant="contained"
                  >
                    Back To Subuser’s Page
                  </Button>
                </RouteLink>
                <IconButton
                  color="inherit"
                  sx={{ p: 0, ml: 2 }}
                  onClick={handleRemoveSubuserId}
                >
                  <Close fontSize="small" />
                </IconButton>
              </FlexCentered>
            )
          }
          severity={isSubUserRoute ? "info" : "warning"}
        >
          {isSubUserRoute ? (
            <>
              You are viewing as{" "}
              <RouteLink
                to="/subuser/user/$userid/detail"
                params={{ userid: String(data.id) }}
              >
                <Link component="span">
                  {data.label} (#{data.id})
                </Link>
              </RouteLink>
              .
            </>
          ) : (
            "View as sub-user is not available on this page. "
          )}
        </Alert>
      </FlexBox>
    </Box>
  );
}
